import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import StripePaymentForm from '../components/StripePaymentForm';
import FundraiserDonationForm from '../components/FundraiserDonationForm';
import PaymentBreakdown from '../components/PaymentBreakdown';

// The registration form submits via a button onClick, not a native <form onSubmit>,
// so the browser's built-in type="email" constraint validation never actually runs -
// without this, any string (e.g. a value accidentally left in the wrong field) sails
// through to the Stripe charge and only fails afterward when creating the account,
// leaving a paid charge with no registration on file.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value) => EMAIL_REGEX.test(String(value || '').trim());

const PublicEvent = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [token, setToken] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState(['stripe']);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  const [venmoPaymentInfo, setVenmoPaymentInfo] = useState(null);
  const [linkedPlayers, setLinkedPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [showNewPlayerForm, setShowNewPlayerForm] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  // Walk-up mode: staff opens the share-link page in a new tab with `walkUp=1` so
  // they can register someone on the spot. In this mode we behave exactly like a
  // guest sign-up regardless of the staff member's logged-in session — the form
  // collects parent + player info from scratch and we omit the auth header on the
  // registration request so the server treats it as a guest registration.
  const isWalkUp = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('walkUp') === '1';
  }, []);

  // For walk-up, ignore the logged-in user entirely.
  const effectiveUser = isWalkUp ? null : user;

  // Fetch linked players for logged-in parents, coaches, or app_managers.
  // Skipped in walk-up mode — walk-ups always use the guest form.
  useEffect(() => {
    if (isWalkUp) return;
    if (user && (user.role === 'parent' || user.role === 'coach' || user.role === 'app_manager')) {
      // For coaches and app_managers, show the form immediately (they likely don't have ParentPlayer relationships)
      // The API call will update this if they do have players
      if (user.role === 'coach' || user.role === 'app_manager') {
        console.log(`[PublicEvent] ${user.role} detected, showing new player form by default`);
        setShowNewPlayerForm(true);
      }
      
      if (user.token) {
        fetchLinkedPlayers();
      } else {
        // If no token but user is parent/coach/app_manager, show new player form immediately
        console.log('[PublicEvent] No token available, showing new player form');
        setShowNewPlayerForm(true);
      }
    }
  }, [user]);

  const fetchLinkedPlayers = async () => {
    try {
      setLoadingPlayers(true);
      console.log('[PublicEvent] Fetching linked players for user:', user?.id, 'role:', user?.role, 'token exists:', !!user?.token);
      const response = await axios.get('/api/parent-player/my-players', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      const players = response.data.players || [];
      console.log('[PublicEvent] Fetched linked players:', players.length, 'players');
      setLinkedPlayers(players);
      // If no players found, automatically show the new player form
      if (players.length === 0) {
        console.log('[PublicEvent] No players found, showing new player form');
        setShowNewPlayerForm(true);
      } else {
        // If players found, hide the new player form and show dropdown instead
        console.log('[PublicEvent] Players found, showing dropdown');
        setShowNewPlayerForm(false);
      }
    } catch (error) {
      console.error('[PublicEvent] Error fetching linked players:', error.response?.status, error.response?.data || error.message);
      // On error (e.g., 403 for non-parents), show the new player form so user can still register
      console.log('[PublicEvent] Error occurred, showing new player form as fallback');
      setShowNewPlayerForm(true);
      setLinkedPlayers([]); // Ensure empty array so form shows
    } finally {
      setLoadingPlayers(false);
      console.log('[PublicEvent] Finished loading players, showNewPlayerForm:', showNewPlayerForm, 'linkedPlayers:', linkedPlayers.length);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    setToken(t);
    if (!t) {
      setError('Missing event token');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/api/public/events/${encodeURIComponent(t)}`);
        setEventData(res.data);
      } catch (e) {
        console.error(e);
        setError('Invalid or expired link');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const [guest, setGuest] = useState(() => {
    if (effectiveUser) {
      return {
        parentName: effectiveUser.role === 'parent' ? effectiveUser.name : '',
        parentEmail: effectiveUser.email || '',
        playerName: effectiveUser.role === 'player' ? effectiveUser.name : '',
        playerDob: effectiveUser.date_of_birth || ''
      };
    }
    return { parentName: '', parentEmail: '', playerName: '', playerDob: '' };
  });

  useEffect(() => {
    if (effectiveUser) {
      setGuest(prev => ({
        parentName: effectiveUser.role === 'parent' ? effectiveUser.name : prev.parentName,
        parentEmail: effectiveUser.email || prev.parentEmail,
        playerName: effectiveUser.role === 'player' ? effectiveUser.name : prev.playerName,
        playerDob: effectiveUser.date_of_birth || prev.playerDob
      }));
    }
  }, [effectiveUser]);

  useEffect(() => {
    if (!eventData) {
      setAvailablePaymentMethods(['stripe']);
      setSelectedPaymentMethod('stripe');
      return;
    }

    const methods = ['stripe'];
    if (eventData.paymentOptions?.venmo?.available) {
      methods.push('venmo');
    }
    setAvailablePaymentMethods(methods);
    setSelectedPaymentMethod(methods[0]);
  }, [eventData]);

  useEffect(() => {
    if (selectedPaymentMethod !== 'stripe') {
      if (stripeConfig) {
        setStripeConfig(null);
      }
      if (pendingRegistration) {
        setPendingRegistration(null);
      }
    }
  }, [selectedPaymentMethod, stripeConfig, pendingRegistration]);

  const buildRegistrationPayload = () => {
    if (!eventData) return null;
    const eventFee = Number(eventData.registrationFee || 0);
    const payload = { amount: eventFee };
    if (isWalkUp) payload.walkUp = true;

    if (!effectiveUser) {
      if (!guest.parentEmail) {
        toast.error('Please enter the parent email to continue.');
        return null;
      }
      if (!isValidEmail(guest.parentEmail)) {
        toast.error('Please enter a valid parent email address.');
        return null;
      }
      if (!guest.playerName || !guest.playerDob) {
        toast.error('Please enter player name and date of birth.');
        return null;
      }
      payload.guest = guest;
      return payload;
    }

    {
      const user = effectiveUser;
      // Always require email for receipt purposes
      const email = guest.parentEmail || user.email;
      if (!email) {
        toast.error('Please enter your email to receive a receipt.');
        return null;
      }
      if (!isValidEmail(email)) {
        toast.error('Please enter a valid email address.');
        return null;
      }

      if (user.role === 'parent' || user.role === 'coach' || user.role === 'app_manager') {
        // If a player is selected from linked players, use their ID
        if (selectedPlayerId) {
          payload.playerUserId = parseInt(selectedPlayerId, 10);
          payload.guest = {
            parentName: user.name,
            parentEmail: email
          };
        } else {
          // New player - require name and DOB
          // Trim whitespace to catch empty strings
          const playerName = (guest.playerName || '').trim();
          const playerDob = (guest.playerDob || '').trim();
          if (!playerName || !playerDob) {
            toast.error('Please enter player name and date of birth to register a new player for this event.');
            return null;
          }
          payload.guest = {
            parentName: user.name,
            parentEmail: email,
            playerName: (guest.playerName || '').trim(),
            playerDob: (guest.playerDob || '').trim()
          };
        }
      } else if (user.role === 'player') {
        // For players, include their email for receipt
        payload.guest = {
          parentName: user.name,
          parentEmail: email,
          playerName: user.name,
          playerDob: user.date_of_birth || ''
        };
      } else {
        // For other roles (org_admin, etc.), if registering a walk-up player, require name and DOB
        // Check if user is entering a new player (has playerName/playerDob in guest state)
        const playerName = (guest.playerName || '').trim();
        const playerDob = (guest.playerDob || '').trim();
        
        if (playerName && playerDob) {
          // User is registering a new player - include their info
          payload.guest = {
            parentName: user.name,
            parentEmail: email,
            playerName: playerName,
            playerDob: playerDob
          };
        } else {
          // No player info provided - this is an error for walk-up registrations
          toast.error('Please enter player name and date of birth to register a player for this event.');
          return null;
        }
      }
    }

    return payload;
  };

  const renderPaymentMethodOptions = () => {
    if (!eventData) return null;
    if ((eventData.registrationFee || 0) <= 0) return null;
    if (availablePaymentMethods.length <= 1) return null;

    return (
      <div className="mt-4 space-y-3">
        <div className="text-sm font-semibold text-gray-700">Choose payment method</div>
        <div className="flex flex-col sm:flex-row gap-3">
          {availablePaymentMethods.map(method => {
            const isSelected = selectedPaymentMethod === method;
            let title = '';
            let description = '';

            if (method === 'stripe') {
              title = 'Credit or Debit Card';
              description = 'Pay securely with card, Apple Pay, or Google Pay.';
              if (eventData.cardPayment?.processingFee > 0) {
                description += ` Includes a $${Number(eventData.cardPayment.processingFee).toFixed(2)} processing fee.`;
              }
            } else if (method === 'venmo') {
              title = 'Venmo';
              description = `Send payment directly to @${eventData.paymentOptions?.venmo?.username || 'coach'} after registering.`;
            } else {
              title = method;
            }

            return (
              <label
                key={method}
                className={`flex items-start gap-3 border rounded-lg px-4 py-3 cursor-pointer transition ${
                  isSelected ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                <input
                  type="radio"
                  name="event-payment-method"
                  value={method}
                  checked={isSelected}
                  onChange={() => setSelectedPaymentMethod(method)}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-semibold text-gray-900">{title}</div>
                  {description && <div className="text-xs text-gray-600 mt-1">{description}</div>}
                </div>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const completeRegistration = async (payload) => {
    if (!token) {
      console.error('[PublicEvent] No token available for registration');
      return;
    }
    setIsProcessing(true);
    try {
      console.log('[PublicEvent] ========== CALLING completeRegistration ==========');
      console.log('[PublicEvent] Payload:', JSON.stringify(payload, null, 2));
      console.log('[PublicEvent] Token:', token.substring(0, 20) + '...');
      
      // Walk-up registrations should behave exactly like a guest sign-up — don't
      // forward the staff member's auth token so the server uses the guest path.
      const headers = !isWalkUp && user?.token
        ? { Authorization: `Bearer ${user.token}` }
        : undefined;

      console.log('[PublicEvent] Making POST request to /api/public/events/' + encodeURIComponent(token) + '/register');
      const res = await axios.post(`/api/public/events/${encodeURIComponent(token)}/register`, payload, { headers });
      console.log('[PublicEvent] Registration response:', res.data);

      if (res.data?.isFree) {
        toast.success('Registration successful! This is a free event.');
        setTimeout(() => {
          window.location.href = '/public/thanks?registration=success';
        }, 1500);
        return;
      }

      if (res.data?.paidViaStripe) {
        toast.success('Registration and payment complete!');
        setStripeConfig(null);
        setPendingRegistration(null);
        setSelectedPaymentMethod('stripe');
        setTimeout(() => {
          window.location.href = '/public/thanks?registration=success';
        }, 1500);
        return;
      }

      if (res.data?.venmoPayment) {
        toast.success('Registration submitted! Complete your Venmo payment below.');
        setVenmoPaymentInfo(res.data.venmoPayment);
        return;
      }

      if (res.data?.publicCheckoutUrl) {
        window.location.href = res.data.publicCheckoutUrl;
        return;
      }

      toast.success('Registration submitted');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Registration failed');
    } finally {
      setIsProcessing(false);
    }
  };


  const handleRegister = async () => {
    const payload = buildRegistrationPayload();
    if (!payload) return;

    setIsProcessing(true);

    try {
      if ((eventData?.registrationFee || 0) > 0) {
        // Venmo: skip Stripe entirely, submit registration directly with payment method preference
        if (selectedPaymentMethod === 'venmo') {
          await completeRegistration({ ...payload, preferredPaymentMethod: 'venmo' });
          return;
        }

        // Stripe: create payment intent first, then show payment form
        const headers = !isWalkUp && user?.token
          ? { Authorization: `Bearer ${user.token}` }
          : undefined;

        const response = await axios.post(
          `/api/public/events/${encodeURIComponent(token)}/stripe-payment-intent`,
          {
            guest: payload.guest || null
          },
          { headers }
        );

        if (!response.data?.paymentIntentId) {
          throw new Error('Payment intent creation failed: No payment intent ID returned');
        }

        setStripeConfig({
          clientSecret: response.data.clientSecret,
          publishableKey: response.data.publishableKey,
          amount: response.data.amount,
          currency: response.data.currency || 'usd',
          breakdown: response.data.breakdown || null
        });
        setPendingRegistration({
          payload,
          paymentIntentId: response.data.paymentIntentId
        });
        toast.success('Please complete payment below to finish registration.');
      } else {
        // Free event: register immediately
        await completeRegistration(payload);
      }
    } catch (error) {
      console.error('[PublicEvent] Registration error:', error);
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentComplete = async (result) => {
    if (!pendingRegistration || !token) return;

    // Payment completed - now register
    if (result?.paymentIntent) {
      const status = result.paymentIntent.status;
      
      if (status === 'succeeded' || status === 'processing') {
        // Payment succeeded - now complete registration
        try {
          await completeRegistration({
            ...pendingRegistration.payload,
            stripePaymentIntentId: result.paymentIntent.id
          });
          
          // Registration will handle redirect in completeRegistration
          setStripeConfig(null);
          setPendingRegistration(null);
        } catch (error) {
          console.error('[PublicEvent] Registration after payment error:', error);
          toast.error('Payment succeeded but registration failed. Please contact support.');
        }
      } else if (status === 'requires_payment_method') {
        toast.error('Payment method required. Please try again.');
      } else {
        toast.error(`Payment status: ${status}. Please check your payment or contact support.`);
      }
    } else if (result?.error) {
      console.error('[PublicEvent] Payment error:', result.error);
      toast.error(result.error || 'Payment failed. Please try again.');
    }
  };

  const paymentInProgress = useMemo(() => !!stripeConfig, [stripeConfig]);
  const isFundraiser = eventData?.type === 'fundraiser';
  const isLesson = eventData?.type === 'lesson';
  const lessonFull = isLesson && eventData?.lesson && eventData.lesson.spotsRemaining <= 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="max-w-3xl mx-auto px-4 pt-10 pb-16">
        {eventData?.organization?.publicSlug && (
          <a
            href="/"
            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            <span aria-hidden="true">←</span>
            Back to {eventData.organization.name}
          </a>
        )}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              {eventData?.organization?.logo && (
                <img
                  src={eventData.organization.logo}
                  alt={`${eventData.organization.name} logo`}
                  className="h-12 w-12 rounded-lg object-contain"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{eventData.title}</h1>
                {eventData?.organization ? (
                  <p className="text-gray-600 mt-1">Hosted by {eventData.organization.name}</p>
                ) : eventData?.team ? (
                  <p className="text-gray-600 mt-1">Hosted by {eventData.team.name}</p>
                ) : (
                  <p className="text-gray-600 mt-1">Public Event</p>
                )}
              </div>
            </div>
            {eventData.registrationFee > 0 && (
              <div className="text-right">
                <div className="text-sm text-gray-500">{isLesson ? 'Lesson Fee' : 'Fee'}</div>
                <div className="text-xl font-semibold">${Number(eventData.registrationFee).toFixed(2)}</div>
                {eventData.cardPayment?.processingFee > 0 && (
                  <div className="text-xs text-gray-500">+ ${Number(eventData.cardPayment.processingFee).toFixed(2)} card processing fee</div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">Date</div>
              <div className="text-sm font-medium text-gray-900">{eventData.date}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Time</div>
              <div className="text-sm font-medium text-gray-900">{eventData.time || 'TBD'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Location</div>
              <div className="text-sm font-medium text-gray-900">{eventData.location}</div>
            </div>
            {eventData?.organization ? (
              <div>
                <div className="text-sm text-gray-500">Organization</div>
                <div className="text-sm font-medium text-gray-900">{eventData.organization.name}</div>
              </div>
            ) : eventData?.team ? (
              <div>
                <div className="text-sm text-gray-500">Team</div>
                <div className="text-sm font-medium text-gray-900">{eventData.team.sport} • {eventData.team.ageGroup} • {eventData.team.season}</div>
              </div>
            ) : null}
          </div>

          {isLesson && eventData.lesson && (
            <div className={`mt-6 p-3 rounded-lg border text-sm ${lessonFull ? 'bg-red-50 border-red-200 text-red-800' : 'bg-teal-50 border-teal-200 text-teal-900'}`}>
              {lessonFull
                ? 'This lesson is full.'
                : `${eventData.lesson.spotsRemaining} of ${eventData.lesson.maxSpots} spots left`}
            </div>
          )}

          {eventData.description && (
            <div className="mt-6">
              <div className="text-sm text-gray-500">About</div>
              <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{eventData.description}</p>
            </div>
          )}

          {isFundraiser && (
            <div className="mt-8">
              <FundraiserDonationForm token={token} fundraiser={eventData.fundraiser} payerCoversFees={eventData.payerCoversFees} />
            </div>
          )}

          {!isFundraiser && !lessonFull && (
          <div className="mt-8">
            {!venmoPaymentInfo && (
            <>
            {isWalkUp && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold text-amber-900">Walk-up registration</p>
                <p className="text-xs text-amber-800 mt-1">
                  You are registering a player on-site. Fill in the parent and player information below — this works exactly like an at-home guest sign-up.
                </p>
              </div>
            )}
            {effectiveUser ? (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Logged in as:</strong> {user.name} ({user.email})
                  </p>
                  {user.role === 'player' && (
                    <p className="text-xs text-blue-700 mt-1">
                      You will be registered as the player for this event.
                    </p>
                  )}
                  {(user.role === 'parent' || user.role === 'coach') && (
                    <p className="text-xs text-blue-700 mt-1">
                      Select a player from your profile or add a new player to register them.
                    </p>
                  )}
                </div>
                <div className="space-y-3">
                  {/* Email field for receipt purposes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address (for receipt) *
                    </label>
                    <input
                      className="border rounded px-3 py-2 w-full"
                      placeholder="your@email.com"
                      type="email"
                      value={guest.parentEmail}
                      onChange={e => setGuest(g => ({ ...g, parentEmail: e.target.value }))}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      We'll send a receipt to this email address after payment
                    </p>
                  </div>
                  {(user.role === 'parent' || user.role === 'coach') && (
                    <div className="space-y-3">
                      {/* Debug info - remove in production */}
                      {process.env.NODE_ENV === 'development' && (
                        <div className="text-xs text-gray-400 p-2 bg-gray-50 rounded">
                          Debug: role={user.role}, loading={loadingPlayers ? 'true' : 'false'}, 
                          players={linkedPlayers.length}, showForm={showNewPlayerForm ? 'true' : 'false'}
                        </div>
                      )}
                      
                      {/* Loading state */}
                      {loadingPlayers && (
                        <p className="text-sm text-gray-500">Loading your players...</p>
                      )}
                      
                      {/* Player Selection - Show dropdown if we have players and not showing new form */}
                      {!loadingPlayers && linkedPlayers.length > 0 && !showNewPlayerForm && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Player *
                          </label>
                          <select
                            className="border rounded px-3 py-2 w-full"
                            value={selectedPlayerId || ''}
                            onChange={(e) => {
                              const playerId = e.target.value;
                              if (playerId === 'new') {
                                setShowNewPlayerForm(true);
                                setSelectedPlayerId(null);
                              } else {
                                setSelectedPlayerId(playerId);
                                const selectedPlayer = linkedPlayers.find(
                                  p => p.player.id === parseInt(playerId, 10)
                                );
                                if (selectedPlayer) {
                                  setGuest(prev => ({
                                    ...prev,
                                    playerName: selectedPlayer.player.name,
                                    playerDob: selectedPlayer.player.date_of_birth || ''
                                  }));
                                }
                              }
                            }}
                            required={!showNewPlayerForm}
                          >
                            <option value="">-- Select a player --</option>
                            {linkedPlayers.map((item) => (
                              <option key={item.player.id} value={item.player.id}>
                                {item.player.name}
                                {item.player.date_of_birth ? ` (DOB: ${item.player.date_of_birth})` : ''}
                              </option>
                            ))}
                            <option value="new">+ Add New Player</option>
                          </select>
                        </div>
                      )}

                      {/* New Player Form - Show when no players OR when explicitly showing new form */}
                      {/* Always show form for coaches/parents when no players are linked, or when explicitly showing new form */}
                      {/* For coaches and app_managers, show form immediately (even during loading) since they likely don't have linked players */}
                      {(((user.role === 'coach' || user.role === 'app_manager') && (showNewPlayerForm || linkedPlayers.length === 0)) || (!loadingPlayers && (showNewPlayerForm || linkedPlayers.length === 0))) && (
                        <div className="space-y-3">
                          {linkedPlayers.length > 0 && showNewPlayerForm && (
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-gray-600">Add a new player to your profile</p>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowNewPlayerForm(false);
                                  setSelectedPlayerId(null);
                                  setGuest(prev => ({ ...prev, playerName: '', playerDob: '' }));
                                }}
                                className="text-sm text-primary-600 hover:text-primary-700"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {linkedPlayers.length === 0 && (
                            <p className="text-sm text-gray-600 mb-2">
                              {(user.role === 'coach' || user.role === 'app_manager')
                                ? 'Register a player for this event. The player will be added to your profile.'
                                : 'Add a new player to your profile to register them for this event.'}
                            </p>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Player Name *
                              </label>
                              <input
                                className="border rounded px-3 py-2 w-full"
                                placeholder="Player name"
                                value={guest.playerName}
                                onChange={e => setGuest(g => ({ ...g, playerName: e.target.value }))}
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Player Date of Birth *
                              </label>
                              <input
                                className="border rounded px-3 py-2 w-full"
                                placeholder="YYYY-MM-DD"
                                type="date"
                                value={guest.playerDob}
                                onChange={e => setGuest(g => ({ ...g, playerDob: e.target.value }))}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {renderPaymentMethodOptions()}
                <button
                  onClick={handleRegister}
                  disabled={isProcessing || paymentInProgress}
                  className="w-full sm:w-auto px-6 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-60"
                >
                  {paymentInProgress ? 'Complete payment below' : isLesson ? 'Claim Spot' : user.role === 'player' ? 'Register Me' : 'Register Player'}
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="w-full space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        className="border rounded px-3 py-2"
                        placeholder="Parent name"
                        value={guest.parentName}
                        onChange={e => setGuest(g => ({ ...g, parentName: e.target.value }))}
                      />
                      <input
                        className="border rounded px-3 py-2"
                        placeholder="Parent email"
                        type="email"
                        value={guest.parentEmail}
                        onChange={e => setGuest(g => ({ ...g, parentEmail: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        className="border rounded px-3 py-2"
                        placeholder="Player name"
                        value={guest.playerName}
                        onChange={e => setGuest(g => ({ ...g, playerName: e.target.value }))}
                      />
                      <input
                        className="border rounded px-3 py-2"
                        placeholder="Player DOB (YYYY-MM-DD)"
                        type="date"
                        value={guest.playerDob}
                        onChange={e => setGuest(g => ({ ...g, playerDob: e.target.value }))}
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleRegister}
                    disabled={isProcessing || paymentInProgress}
                    className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-60"
                  >
                    {paymentInProgress ? 'Complete payment below' : isLesson ? 'Claim Spot' : 'Register Now'}
                  </button>
                </div>
                {renderPaymentMethodOptions()}
              </>
            )}
            </>
            )}
          </div>
          )}

          {selectedPaymentMethod === 'stripe' && stripeConfig && (
            <div className="mt-8">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800">
                  Complete the secure payment below to finalize your registration. Once the payment succeeds, we will automatically finish your registration.
                </p>
              </div>
              <PaymentBreakdown
                breakdown={stripeConfig.breakdown}
                baseLabel={isLesson ? 'Lesson fee' : 'Registration fee'}
              />
              <StripePaymentForm
                config={stripeConfig}
                onComplete={handlePaymentComplete}
              />
            </div>
          )}

          {venmoPaymentInfo && (
            <div className="mt-8 p-5 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#3D95CE] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">V</span>
                </div>
                <h3 className="text-base font-semibold text-gray-900">Complete your Venmo payment</h3>
              </div>
              <p className="text-sm text-gray-700">
                Your registration is submitted. Send your payment via Venmo to finish:
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-gray-500">To</div>
                  <div className="font-semibold text-gray-900">@{venmoPaymentInfo.username}</div>
                </div>
                <div>
                  <div className="text-gray-500">Amount</div>
                  <div className="font-semibold text-gray-900">${venmoPaymentInfo.amount}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">Note</div>
                  <div className="font-medium text-gray-900">{venmoPaymentInfo.note}</div>
                </div>
              </div>
              <a
                href={venmoPaymentInfo.appUrl}
                className="block w-full text-center px-4 py-3 rounded-md bg-[#3D95CE] text-white font-semibold hover:bg-[#357ab8] transition-colors"
              >
                Open in Venmo
              </a>
              <a
                href={venmoPaymentInfo.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-4 py-2 rounded-md border border-blue-300 text-blue-700 text-sm hover:bg-blue-100 transition-colors"
              >
                Pay on Venmo.com instead
              </a>
              <p className="text-xs text-gray-500 text-center">
                Your registration is saved. The coach will confirm once payment is received.
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PublicEvent;

